// frontend/src/components/ScientificDashboard/ScientificSelector.tsx

import React from 'react';

export interface TraceItem {
  trace_id: string;
  dataset_name?: string;
  pipeline_version?: number;
  vendor?: string;
  created_at?: string;
  status?: string;
}

interface Props {
  traces: TraceItem[];
  selectedTrace: string | null;
  onSelect: (traceId: string) => void;
  loading: boolean;
}

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getStatusColor = (status?: string): string => {
  switch (status) {
    case 'SUCCESS': return '#66bb6a';
    case 'FAILED': return '#ef5350';
    case 'RUNNING':
    case 'STARTED': return '#4fc3f7';
    case 'SKIPPED': return '#9e9e9e';
    default: return '#8899aa';
  }
};

export const ScientificSelector: React.FC<Props> = ({
  traces,
  selectedTrace,
  onSelect,
  loading,
}) => {
  if (loading && traces.length === 0) {
    return <div style={{ color: '#8899aa', padding: '20px' }}>Carregando traces...</div>;
  }

  if (traces.length === 0) {
    return <div style={{ color: '#8899aa', padding: '20px', textAlign: 'center' }}>Nenhuma execução encontrada</div>;
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ color: '#4fc3f7', marginBottom: '12px' }}>🔍 Selecione uma Execução</h3>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {traces.map((trace) => {
          const isSelected = selectedTrace === trace.trace_id;
          return (
            <button
              key={trace.trace_id}
              onClick={() => onSelect(trace.trace_id)}
              style={{
                padding: '12px 16px',
                background: isSelected
                  ? 'rgba(79, 195, 247, 0.2)'
                  : 'rgba(15, 42, 74, 0.4)',
                border: isSelected
                  ? '1px solid #4fc3f7'
                  : '1px solid #1a4a7a',
                borderRadius: '8px',
                color: isSelected ? '#4fc3f7' : '#8899aa',
                cursor: 'pointer',
                transition: 'all 0.3s',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                minWidth: '280px',
              }}
            >
              {/* Dataset + versão */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>
                  {renderValue(trace.dataset_name)}
                </span>
                {trace.pipeline_version !== undefined && trace.pipeline_version !== null && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '1px 8px',
                    borderRadius: '10px',
                    background: 'rgba(79, 195, 247, 0.15)',
                    color: '#4fc3f7',
                  }}>
                    v{trace.pipeline_version}
                  </span>
                )}
              </div>

              {/* trace_id */}
              <span style={{ fontSize: '11px', opacity: 0.7, fontFamily: 'monospace' }}>
                trace_id: {renderValue(trace.trace_id)}
              </span>

              {/* vendor */}
              {trace.vendor && (
                <span style={{ fontSize: '11px', opacity: 0.7 }}>
                  vendor: {renderValue(trace.vendor)}
                </span>
              )}

              {/* created_at */}
              {trace.created_at && (
                <span style={{ fontSize: '11px', opacity: 0.6, fontFamily: 'monospace' }}>
                  {renderValue(trace.created_at)}
                </span>
              )}

              {/* status */}
              {trace.status && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: getStatusColor(trace.status),
                }}>
                  {renderValue(trace.status)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};